import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { nextSimulationStep } from 'store/simulation/actions/switchSimulationStep'
import { updateSimulationInput } from 'store/simulation/actions/updateSimulationInput'
import { Store } from 'store/types'
import NavigationButtons from '../components/navigation-buttons/NavigationButtons'
import { CounterTypes, MeterType, MeterUsage, UsageTypes } from '../types'
import { checkQualifiedUsage } from '../utils'
import { SimulationInputKeys } from 'store/simulation/types'
import { Region } from 'types/region'
import { MoreInformationPopup } from '../components/more-information-popup/MoreInformationPopup'
import { MIN_USAGE_FOR_USUAL } from '../constants'
import { trackEvent } from 'utils/eventTracking'
import styles from './Steps.module.scss'
import simulationStyles from '../Simulation.module.scss'
import classNames from 'classnames'
import { preventArrowUpAndDownEvent, preventInputScrollEvent } from 'utils/eventHandlers'

export const UsageStep: FC = () => {
  // REDUX STORE
  const { inputs } = useSelector((state: Store) => state.simulation)
  const dispatch = useDispatch()

  // TRANSLATIONS
  const { t } = useTranslation('simulation')
  const placeholder = t('usageInKWH')

  // State
  const [showInfo, setShowInfo] = useState<boolean>(false)
  const [showUnusualUsage, setShowUnusualUsage] = useState<Partial<Record<UsageTypes, boolean>>>({
    [UsageTypes.SINGLE_RATE]: false,
    [UsageTypes.DOUBLE_RATE_DAY]: false,
    [UsageTypes.DOUBLE_RATE_NIGHT]: false,
    [UsageTypes.EXCLUSIVE_NIGHT]: false
  })

  // The unusual usage warning text
  const unusualUsageWarning = (
    <span className="is-red is-block margin-bottom-m">
      {t('unusualUsage', 'Dit verbruik is onwaarschijnlijk tenzij je woning onbewoond is. Zie meer info.')}
    </span>
  )

  /**
   * Returns the title copy based on the region & meter type
   */
  const determineMoreInfoAndGettitleCopy = () => {
    const defaultTitle = t('myUsageTitle')
    const nettoTitle = t('myUsageTitleNetto')
    const brutoTitle = t('myUsageTitleBruto')

    switch (inputs.region) {
      case Region.BRUSSELS:
        if (inputs.hasSolarPanels === true) return brutoTitle

        return defaultTitle

      case Region.FLANDERS:
        if (inputs.hasSolarPanels === true) {
          if (inputs.counterType === CounterTypes.ANALOG) return nettoTitle

          return brutoTitle
        }

        return defaultTitle

      case Region.WALLONIA:
        if (inputs.hasSolarPanels === true) return nettoTitle

        return defaultTitle

      default:
        return defaultTitle
    }
  }

  /**
   * Checks if the next button should be disabled
   */
  const nextDisabled = () => {
    const usage: MeterUsage = inputs.usage

    // Check if single rate values are valid (if necessary)
    if (inputs.meterType === MeterType.SINGLE_RATE) {
      if (usage.singleRate === null || usage.singleRate < 0) return true
    }

    // Check if double rate values are valid (if necessary)
    if (inputs.meterType === MeterType.DOUBLE_RATE) {
      if (usage.doubleRate.day === null || usage.doubleRate.day < 0) return true
      if (usage.doubleRate.night === null || usage.doubleRate.night < 0) return true
    }

    // Check if excl night values are valid (if necessary)
    if (inputs.hasExclusiveNightMeter === true) {
      if (usage.exclNight === null || usage.exclNight < 0) return true
    }

    // Check if gas values are valid (if necessary)
    if (inputs.needsGas) {
      if (usage.gas === null || usage.gas < 0) return true
    }

    return false
  }

  /**
   * Checks if the unusual usage warning for the given type should be shown
   */
  const checkIfUnusualUsageShouldBeShown = (usageType: UsageTypes) => {
    // Get showUnusualUsage state
    const newShowUnusualUsageState = { ...showUnusualUsage }

    // Only show the text if lead has solar panels & counter type is Digital
    if (inputs.hasSolarPanels && (inputs.counterType === CounterTypes.DIGITAL || inputs.region === Region.BRUSSELS) && inputs.usage) {
      switch (usageType) {
        // Single Rate
        case UsageTypes.SINGLE_RATE:
          newShowUnusualUsageState[UsageTypes.SINGLE_RATE] =
            inputs.usage.singleRate !== null && inputs.usage.singleRate < MIN_USAGE_FOR_USUAL
          break

        // Double rate day
        case UsageTypes.DOUBLE_RATE_DAY:
          newShowUnusualUsageState[UsageTypes.DOUBLE_RATE_DAY] =
            inputs.usage.doubleRate.day !== null && inputs.usage.doubleRate.day < MIN_USAGE_FOR_USUAL
          break

        // Double rate night
        case UsageTypes.DOUBLE_RATE_NIGHT:
          newShowUnusualUsageState[UsageTypes.DOUBLE_RATE_NIGHT] =
            inputs.usage.doubleRate.night !== null && inputs.usage.doubleRate.night < MIN_USAGE_FOR_USUAL
          break

        // Exclusive night
        case UsageTypes.EXCLUSIVE_NIGHT:
          newShowUnusualUsageState[UsageTypes.EXCLUSIVE_NIGHT] =
            inputs.usage.exclNight !== null && inputs.usage.exclNight < MIN_USAGE_FOR_USUAL
          break
      }
    }

    setShowUnusualUsage(newShowUnusualUsageState)
  }

  /**
   * Updates the correct usage in the store based on the given key
   *
   * @param key
   * @param value
   */
  const updateUsage = (key: UsageTypes, value: number) => {
    const usage: MeterUsage = inputs.usage

    // Check which to update
    switch (key) {
      case UsageTypes.SINGLE_RATE:
        usage.singleRate = value
        break

      case UsageTypes.DOUBLE_RATE_DAY:
        usage.doubleRate.day = value
        break

      case UsageTypes.DOUBLE_RATE_NIGHT:
        usage.doubleRate.night = value
        break

      case UsageTypes.EXCLUSIVE_NIGHT:
        usage.exclNight = value
        break

      case UsageTypes.GAS:
        usage.gas = value
        break

      default:
        break
    }

    // Update usage
    dispatch(updateSimulationInput({ key: SimulationInputKeys.USAGE, value: usage }))
  }

  /**
   * Handles the simulation button click
   */
  const handleSimulationButtonClick = () => {
    // Track 'Simulation Started' event @Semetis
    trackEvent('simulation_started', {
      consumption: inputs.usage,
      step: 'usage'
    })

    // Go to next step
    dispatch(nextSimulationStep())
  }

  return (
    <div>
      <div className="is-flex-column is-flex-centered">
        <h2 className={classNames(styles.title, 'margin-top-none', 'margin-bottom-l')}>{determineMoreInfoAndGettitleCopy()}</h2>
      </div>
      <div className={styles['simulation-inputs']}>
        {inputs.meterType === MeterType.SINGLE_RATE && (
          <>
            <div>
              <label htmlFor="electricity" className="is-block margin-bottom-xs">
                {t('electricity', 'Elektriciteit')}
              </label>

              <input
                type="number"
                min={0}
                className="border"
                id="electricity"
                pattern="[0-9]*"
                inputMode="numeric"
                required
                placeholder={placeholder}
                value={inputs.usage ? inputs.usage.singleRate : 0}
                onChange={(e) => {
                  updateUsage(UsageTypes.SINGLE_RATE, parseInt(e.currentTarget.value, 10))
                }}
                onBlur={() => checkIfUnusualUsageShouldBeShown(UsageTypes.SINGLE_RATE)}
                onWheel={(e) => preventInputScrollEvent(e)}
                onKeyDown={(e) => preventArrowUpAndDownEvent(e)}
              />
            </div>

            {showUnusualUsage[UsageTypes.SINGLE_RATE] && unusualUsageWarning}
          </>
        )}

        {inputs.meterType === MeterType.DOUBLE_RATE && (
          <>
            <div>
              <label htmlFor="electricityDay" className="is-block margin-bottom-xs">
                {t('electricityDay', 'Elektriciteit (Dag)')}
              </label>

              <input
                type="number"
                min={0}
                className="border"
                id="electricityDay"
                pattern="[0-9]*"
                inputMode="numeric"
                required
                placeholder={placeholder}
                value={inputs.usage ? inputs.usage.doubleRate.day : 0}
                onChange={(e) => {
                  updateUsage(UsageTypes.DOUBLE_RATE_DAY, parseInt(e.currentTarget.value, 10))
                }}
                onBlur={() => checkIfUnusualUsageShouldBeShown(UsageTypes.DOUBLE_RATE_DAY)}
                onWheel={(e) => preventInputScrollEvent(e)}
                onKeyDown={(e) => preventArrowUpAndDownEvent(e)}
              />
            </div>

            {showUnusualUsage[UsageTypes.DOUBLE_RATE_DAY] && unusualUsageWarning}

            <div>
              <label htmlFor="electricityNight" className="is-block margin-bottom-xs">
                {t('electricityNight', 'Elektriciteit (Nacht)')}
              </label>

              <input
                type="number"
                min={0}
                className="border"
                id="electricityNight"
                pattern="[0-9]*"
                inputMode="numeric"
                required
                placeholder={placeholder}
                value={inputs.usage ? inputs.usage.doubleRate.night : 0}
                onChange={(e) => {
                  updateUsage(UsageTypes.DOUBLE_RATE_NIGHT, parseInt(e.currentTarget.value, 10))
                }}
                onBlur={() => checkIfUnusualUsageShouldBeShown(UsageTypes.DOUBLE_RATE_NIGHT)}
                onWheel={(e) => preventInputScrollEvent(e)}
                onKeyDown={(e) => preventArrowUpAndDownEvent(e)}
              />
            </div>

            {showUnusualUsage[UsageTypes.DOUBLE_RATE_NIGHT] && unusualUsageWarning}
          </>
        )}

        {inputs.hasExclusiveNightMeter === true && (
          <>
            <div>
              <label htmlFor="electricityExclusiveNight" className="is-block margin-bottom-xs">
                {t('electricityExclusiveNight', 'Elektriciteit (Exclusief nacht)')}
              </label>
              <input
                type="number"
                min={0}
                className="border"
                id="electricityExclusiveNight"
                pattern="[0-9]*"
                inputMode="numeric"
                required
                placeholder={placeholder}
                value={inputs.usage ? inputs.usage.exclNight : 0}
                onChange={(e) => {
                  updateUsage(UsageTypes.EXCLUSIVE_NIGHT, parseInt(e.currentTarget.value, 10))
                }}
                onBlur={() => checkIfUnusualUsageShouldBeShown(UsageTypes.EXCLUSIVE_NIGHT)}
                onWheel={(e) => preventInputScrollEvent(e)}
                onKeyDown={(e) => preventArrowUpAndDownEvent(e)}
              />
            </div>

            {showUnusualUsage[UsageTypes.EXCLUSIVE_NIGHT] && unusualUsageWarning}
          </>
        )}

        {inputs.needsGas && (
          <>
            <div>
              <label htmlFor="gas" className="is-block margin-bottom-xs">
                {t('gas', 'Gas')}
              </label>
              <input
                type="number"
                min={0}
                className="border"
                id="gas"
                pattern="[0-9]*"
                inputMode="numeric"
                required
                placeholder={placeholder}
                value={inputs.usage ? inputs.usage.gas : 0}
                onChange={(e) => {
                  updateUsage(UsageTypes.GAS, parseInt(e.currentTarget.value, 10))
                }}
                onWheel={(e) => preventInputScrollEvent(e)}
                onKeyDown={(e) => preventArrowUpAndDownEvent(e)}
              />
            </div>
          </>
        )}

        {!checkQualifiedUsage(inputs.usage, inputs.meterType === MeterType.DOUBLE_RATE, inputs.hasExclusiveNightMeter) && (
          <div className="is-red margin-bottom-m">
            {t('moreThan50MWhElectricityOr100MWhGas', 'Wij leveren momenteel enkel aan klanten met een residentieel of beperkt verbruik.')}
          </div>
        )}
      </div>

      <div className="has-text-centered margin-bottom-m">
        <button className={classNames(simulationStyles.link, 'margin-right-s')} onClick={() => setShowInfo(true)}>
          {t('moreInfo.usage.link', 'Meer informatie')}
        </button>
      </div>

      <NavigationButtons nextDisabled={nextDisabled()} onNextClick={() => handleSimulationButtonClick()} />

      <MoreInformationPopup
        isOpen={showInfo}
        onRequestClose={() => setShowInfo(false)}
        i18nKey={`moreInfo.usage.${inputs.hasSolarPanels && inputs.counterType === CounterTypes.ANALOG ? 'netto' : 'bruto'}`}
      />
    </div>
  )
}
